/* eslint class-methods-use-this: "off", no-param-reassign: "off" */
import { PolicyStatusSearchValueMap } from 'client/util/Constants';
import { includes } from 'lodash';
import CollectionModule from 'client/store/modules/CollectionModule';
import PolicySystemStatusApi from 'client/api/v2/PolicySystemStatusApi';

class PolicySystemStatusCollection extends CollectionModule {
  /**
   * Override method from base class to add policies specific fields to the Vuex
   * module entities and features.
   */
  extractModule() {
    const module = super.extractModule();

    module.actions.setPolicyId = (...params) => this.setPolicyId(...params);
    module.actions.fetchFilteredTotalCount = (...params) => this.fetchFilteredTotalCount(...params);

    module.getters.getAppliedFilters = (...params) => this.getAppliedFilters(...params);

    module.mutations.SET_POLICY_ID = (...params) => this.SET_POLICY_ID(...params);
    module.mutations.SET_TOTAL_COUNT = (...params) => this.SET_TOTAL_COUNT(...params);

    return module;
  }

  // --------------------------------------------------------------------------
  // State
  // --------------------------------------------------------------------------

  getInitialState() {
    const state = super.getInitialState();

    state.entities.policyId = '';
    state.features.items.filters = [
      {
        name: 'system_id',
        operator: 'eq',
        value: '',
      },
      {
        name: 'exit_status',
        operator: 'eq',
        value: '',
      },
      {
        name: 'state',
        operator: 'eq',
        value: '',
      },
    ];

    return state;
  }

  // --------------------------------------------------------------------------
  // Actions
  // --------------------------------------------------------------------------

  /**
   * Override action from base class to evaluate if all items have been fetched
   * and update totalCount correctly.
   * This override is needed because policy status values fetched can contain entries
   * related to deleted systems, which are filtered out by PolicySystemStatusApi.
   * This, however, can cause a mismatch between the totalCount received by the backend
   * and the actual total number of entries displayed in the itemTable.
   * We also need to keep the original totalCount so that the itemTable can fetch all
   * the possible status values correctly until we find all possible data.
   */
  async fetchCollection(context, { next = false } = {}) {
    // super.fetchCollection is called at the start,
    // as it sets the totalCount state value that we're checking here.
    const results = await super.fetchCollection(context, { next });

    const { state } = context;
    const { skip, limit } = state.features.items.pagination;
    const { ids } = state.features.items;
    const { totalCount } = state.entities.items;

    const isTotalCountSet = !!totalCount;
    const areAllItemsFetched = skip + limit >= totalCount;

    if (isTotalCountSet && areAllItemsFetched) {
      // this will set the totalCount value to the actual amount of policy status entries
      // with existing devices, but only when all data has been fetched
      context.commit('SET_TOTAL_COUNT', { totalCount: ids.length });
    }

    return results;
  }

  // this gets a filtered total count that removes any "failed to install" duplicate status values
  async fetchFilteredTotalCount() {
    const failedStatusFilter = [
      {
        name: 'state',
        operator: 'eq',
        value: 'FAILED',
      },
    ];

    const failedStatusItems = await this.Api.fetchAll('', '', failedStatusFilter);
    const boundSystems = await this.Api.getBoundSystems();

    // we want to subtract the amount of unbound duplicate policy results
    const unboundFailedDuplicateCount = failedStatusItems.reduce(
      (count, item) => {
        const isDuplicate = item.stdErr.includes('Duplicate');
        const isUnbound = !boundSystems
          .find(boundSystem => boundSystem.id === item.systemID);

        return isDuplicate && isUnbound ? count + 1 : count;
      }, 0,
    );

    const totalCount = await this.Api.fetchTotalCount();
    return totalCount - unboundFailedDuplicateCount;
  }

  setPolicyId({ commit }, policyId) {
    commit('SET_POLICY_ID', policyId);
  }

  // override setSearchTerm to allow autofill of filter name
  setSearchTerm(context, { searchTerm, preventReset }) {
    const previousSearchTerm = context.getters.getSearchTerm;

    if (!preventReset) {
      context.commit('RESET_COLLECTION', { soft: true });
    }

    if (searchTerm) {
      if (includes(searchTerm, ':')) {
        const searchTermArray = searchTerm.split(':');
        if (PolicyStatusSearchValueMap[searchTermArray[0]]) {
          const name = PolicyStatusSearchValueMap[searchTermArray[0]];
          const value = searchTermArray[1].trim();
          const filter = { name, operator: 'eq', value };

          context.commit('SET_FILTER', { filter });
        }
      }
    // remove filter if the last search included a search term
    } else if (includes(previousSearchTerm, ':')) {
      const filterTerm = previousSearchTerm.split(':')[0];
      const filterName = PolicyStatusSearchValueMap[filterTerm];
      if (filterName) context.commit('DELETE_FILTER', { filterName });
    }
    context.commit('SET_SEARCH_TERM', { searchTerm });
  }

  // --------------------------------------------------------------------------
  // Getters
  // --------------------------------------------------------------------------

  getAppliedFilters(state = {
    features: {
      items: {
        filters: [],
      },
    },
  }) {
    const { filters } = state.features.items;
    return filters.filter(filter => filter.name === 'state' && filter.value !== '');
  }

  // --------------------------------------------------------------------------
  // Mutations
  // --------------------------------------------------------------------------

  SET_POLICY_ID(state = {
    entities: {},
  }, policyId) {
    state.entities.policyId = policyId;
    this.Api.setPolicyId(policyId);
  }

  SET_TOTAL_COUNT(state, { totalCount }) {
    if (typeof totalCount === 'number') {
      state.entities.items.totalCount = totalCount;
    }
  }
}

export default new PolicySystemStatusCollection(PolicySystemStatusApi, 'id', '').extractModule();

// Export class so that private methods used by actions can be tested!
export const PolicySystemStatusCollectionClass = PolicySystemStatusCollection;
